import React, {useState} from 'react';
import * as Yup from 'yup';
import {Form, Row} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import {useFormik} from 'formik';
import toast from 'react-hot-toast';
import TextAreaInputControlled from '../../../app/modules/form/TextAreaInputControlled';
import FileInputControlled from '../../../app/modules/form/FileInputControlled';
import RadioInputControlled from '../../../app/modules/form/RadioInputControlled';
import CheckboxGroupControlled from '../../../app/modules/form/CheckboxGroupControlled';
import ButtonSubmit from '../../../app/modules/form/ButtonSubmit';
import ButtonReset from '../../../app/modules/form/ButtonReset';
import {useSubmitApsRequest} from '../Hooks/DivopsHooks';
import {AdresseSuggestion, ApsSpofValue, SelectOption} from '../Types/types';
import {Customer} from '../../Auth/Types/customer';
import AdresseAutocomplete from './AdresseAutocomplete';

const SERVICE_OPTIONS: SelectOption[] = [
  {value: 'FON', label: 'FON'},
  {value: 'LAMBDA', label: 'LAMBDA'},
  {value: 'VPN', label: 'VPN'},
];

const DUREE_OPTIONS: SelectOption[] = [
  {value: '12 mois', label: '12 mois'},
  {value: '36 mois', label: '36 mois'},
  {value: '60 mois', label: '60 mois'},
];

const TYPE_OPTIONS: SelectOption[] = [
  {value: 'Location', label: 'Location'},
  {value: 'IRU 10 ans', label: 'IRU 10 ans'},
  {value: 'IRU 15 ans', label: 'IRU 15 ans'},
];

const OPTIONS_SECURISATION = [
  {
    value: 'Double Parcours',
    label: 'Double Parcours',
    description:
      "Redondance physique du chemin de câble entre le site client et le point de raccordement : la fibre est déployée selon deux itinéraires physiquement distincts (fourreaux, chambres, façades différentes), de sorte qu'une coupure accidentelle (travaux, incident de génie civil, sinistre) sur un tracé n'affecte pas l'autre. Cela protège contre la coupure du câble lui-même, sur le « dernier kilomètre » entre le site et le réseau. C'est ce que couvre chez Celeste l'option Fibre optique CELESTE 2 pour double parcours optique ≤1G",
  },
  {
    value: 'Double POP',
    label: 'Double POP',
    description:
      "Redondance au niveau du réseau : le lien du client est raccordé à deux Points de Présence (POP) distincts de l'opérateur, plutôt qu'à un seul. Cela protège contre une panne ou un incident affectant un POP entier (coupure électrique, défaillance d'équipement, sinistre sur le site du POP), en garantissant que le second accès reste opérationnel via l'autre POP. C'est l'option chez Celeste  : Livraison sur deux POP différents - Fibre CELESTE.",
  },
];

interface ApsFormValues {
  buId: string;
  adresse: string;
  serviceSouhaite: string;
  nombreBrins: string;
  spof: ApsSpofValue | '';
  optionsSecurisation: string[];
  budget: string;
  delai: string;
  informationComplementaire: string;
  duree: string;
  type: string;
  file?: File;
}

const DEFAULT_VALUES: ApsFormValues = {
  buId: '',
  adresse: '',
  serviceSouhaite: '',
  nombreBrins: '',
  spof: '',
  optionsSecurisation: [],
  budget: '',
  delai: '',
  informationComplementaire: '',
  duree: '',
  type: '',
  file: undefined,
};

interface ApsRequestFormProps {
  /** BU fixe (modal). Ignorée si `buOptions` est fourni. */
  buId?: number;
  buName?: string;
  /** Page dédiée : affiche le champ adresse et le choix de la BU (masqué s'il n'y en a qu'une). */
  buOptions?: Customer[];
  withAddress?: boolean;
  onSuccess: () => void;
  onCancel: () => void;
}

const ApsRequestForm = ({
  buId,
  buName,
  buOptions,
  withAddress = false,
  onSuccess,
  onCancel,
}: ApsRequestFormProps): JSX.Element => {
  const intl = useIntl();
  const requiredFieldMessage = intl.formatMessage({id: 'FIELD.REQUIRED'});
  const {mutate, isLoading} = useSubmitApsRequest();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const hasBuChoice = buOptions !== undefined;
  const singleBu = buOptions?.length === 1 ? buOptions[0] : undefined;

  const validationSchema = Yup.object().shape({
    buId: hasBuChoice && !singleBu ? Yup.string().required(requiredFieldMessage) : Yup.string(),
    adresse: withAddress ? Yup.string().required(requiredFieldMessage) : Yup.string(),
    serviceSouhaite: Yup.string().required(requiredFieldMessage),
    nombreBrins: Yup.string().when('serviceSouhaite', {
      is: 'FON',
      then: Yup.string().required(requiredFieldMessage),
    }),
    spof: Yup.string().required(requiredFieldMessage),
    budget: Yup.string().required(requiredFieldMessage),
    delai: Yup.string().required(requiredFieldMessage),
    duree: Yup.string().required(requiredFieldMessage),
    type: Yup.string().required(requiredFieldMessage),
  });

  const formik = useFormik<ApsFormValues>({
    initialValues: {...DEFAULT_VALUES, buId: singleBu ? singleBu.id.toString() : ''},
    validationSchema,
    onSubmit: (values) => {
      const selectedBu = hasBuChoice
        ? buOptions?.find((bu) => bu.id.toString() === values.buId)
        : undefined;
      const resolvedBuId = selectedBu?.id ?? buId;
      if (resolvedBuId === undefined) {
        return;
      }
      mutate(
        {
          clientId: resolvedBuId.toString(),
          customerId: resolvedBuId,
          buName: selectedBu?.name ?? buName ?? '',
          adresse: withAddress ? values.adresse : undefined,
          serviceSouhaite: values.serviceSouhaite,
          nombreBrins: values.nombreBrins ? Number(values.nombreBrins) : undefined,
          spof: values.spof as ApsSpofValue,
          optionsSecurisation: values.optionsSecurisation,
          budget: values.budget,
          delai: values.delai,
          informationComplementaire: values.informationComplementaire || undefined,
          duree: values.duree,
          type: values.type,
          file: values.file,
        },
        {
          onSuccess: (data) => {
            setErrorMessage(null);
            const ticketNumber = data?.TicketNumber;
            toast.success(
              ticketNumber
                ? intl.formatMessage({id: 'DIVOPS.APS.SUCCESS.WITH_NUMBER'}, {number: ticketNumber})
                : intl.formatMessage({id: 'DIVOPS.APS.SUCCESS'})
            );
            formik.resetForm();
            onSuccess();
          },
          onError: () => {
            const message = intl.formatMessage({id: 'DIVOPS.APS.ERROR'});
            setErrorMessage(message);
            toast.error(message);
          },
        }
      );
    },
  });

  const handleFileChange = (e: React.ChangeEvent<Element>) => {
    const target = e.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      formik.setFieldValue('file', target.files[0]);
    }
  };

  const handleClose = () => {
    setErrorMessage(null);
    formik.resetForm();
    onCancel();
  };

  const handleAddressSelect = (suggestion: AdresseSuggestion) => {
    formik.setFieldValue('adresse', suggestion.label);
  };

  return (
    <>
          {hasBuChoice && !singleBu && (
            <Row className="mb-3">
              <Form.Group>
                <Form.Label className="fw-bold fs-3 required">
                  {intl.formatMessage({id: 'DIVOPS.APS.COMPTE'})}
                </Form.Label>
                <Form.Select
                  name="buId"
                  value={formik.values.buId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={formik.touched.buId && !!formik.errors.buId}
                >
                  <option value="">{intl.formatMessage({id: 'DIVOPS.APS.COMPTE.PLACEHOLDER'})}</option>
                  {buOptions?.map((bu) => (
                    <option key={bu.id} value={bu.id}>
                      {bu.id} - {bu.name}
                    </option>
                  ))}
                </Form.Select>
                {formik.touched.buId && formik.errors.buId && (
                  <Form.Control.Feedback type="invalid">{formik.errors.buId}</Form.Control.Feedback>
                )}
              </Form.Group>
            </Row>
          )}

          {withAddress && (
            <Row className="mb-3">
              <Form.Group>
                <Form.Label className="fw-bold fs-3 required">
                  {intl.formatMessage({id: 'DIVOPS.APS.ADRESSE'})}
                </Form.Label>
                <AdresseAutocomplete
                  placeholder={intl.formatMessage({id: 'DIVOPS.ELIGIBILITY.ADRESSE.PLACEHOLDER'})}
                  onSelect={handleAddressSelect}
                />
                {formik.touched.adresse && formik.errors.adresse && (
                  <div className="text-danger fs-8 mt-1">{formik.errors.adresse}</div>
                )}
              </Form.Group>
            </Row>
          )}

          <Row className="mb-3">
            <Form.Group>
              <Form.Label className="fw-bold fs-3 required">
                {intl.formatMessage({id: 'DIVOPS.APS.SERVICE'})}
              </Form.Label>
              <Form.Select
                name="serviceSouhaite"
                value={formik.values.serviceSouhaite}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.serviceSouhaite && !!formik.errors.serviceSouhaite}
              >
                <option value="">
                  {intl.formatMessage({id: 'DIVOPS.APS.SERVICE.PLACEHOLDER'})}
                </option>
                {SERVICE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              {formik.touched.serviceSouhaite && formik.errors.serviceSouhaite && (
                <Form.Control.Feedback type="invalid">
                  {formik.errors.serviceSouhaite}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Row>

          {formik.values.serviceSouhaite === 'FON' && (
            <Row className="mb-3">
              <Form.Group>
                <Form.Label className="fw-bold fs-3 required">
                  {intl.formatMessage({id: 'DIVOPS.APS.NOMBRE_BRINS'})}
                </Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  name="nombreBrins"
                  value={formik.values.nombreBrins}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={formik.touched.nombreBrins && !!formik.errors.nombreBrins}
                />
                {formik.touched.nombreBrins && formik.errors.nombreBrins && (
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.nombreBrins}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Row>
          )}

          <Row className="mb-3">
            <RadioInputControlled
              name="spof"
              label={intl.formatMessage({id: 'DIVOPS.APS.SPOF'})}
              description="Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua."
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              onReset={formik.handleReset}
              touched={formik.touched.spof}
              errors={formik.errors.spof}
              options={[
                {label: intl.formatMessage({id: 'DIVOPS.APS.YES'}), value: 'oui'},
                {label: intl.formatMessage({id: 'DIVOPS.APS.NO'}), value: 'non'},
              ]}
            />
          </Row>

          <Row className="mb-3">
            <CheckboxGroupControlled
              name="optionsSecurisation"
              label={intl.formatMessage({id: 'DIVOPS.APS.OPTIONS_SECURISATION'})}
              required={false}
              values={formik.values.optionsSecurisation}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              onReset={formik.handleReset}
              touched={formik.touched.optionsSecurisation as boolean | undefined}
              errors={formik.errors.optionsSecurisation as string | undefined}
              options={OPTIONS_SECURISATION}
            />
          </Row>

          <Row className="mb-3">
            <Form.Group>
              <Form.Label className="fw-bold fs-3 required">
                {intl.formatMessage({id: 'DIVOPS.APS.BUDGET'})}
              </Form.Label>
              <div className="input-group">
                <Form.Control
                  type="number"
                  min={0}
                  name="budget"
                  value={formik.values.budget}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={formik.touched.budget && !!formik.errors.budget}
                />
                <span className="input-group-text">EUR</span>
              </div>
              {formik.touched.budget && formik.errors.budget && (
                <Form.Control.Feedback type="invalid" style={{display: 'block'}}>
                  {formik.errors.budget}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group>
              <Form.Label className="fw-bold fs-3 required">
                {intl.formatMessage({id: 'DIVOPS.APS.DELAI'})}
              </Form.Label>
              <Form.Control
                type="text"
                name="delai"
                value={formik.values.delai}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.delai && !!formik.errors.delai}
                placeholder={intl.formatMessage({id: 'DIVOPS.APS.DELAI.PLACEHOLDER'})}
              />
              {formik.touched.delai && formik.errors.delai && (
                <Form.Control.Feedback type="invalid">{formik.errors.delai}</Form.Control.Feedback>
              )}
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group>
              <Form.Label className="fw-bold fs-3 required">
                {intl.formatMessage({id: 'DIVOPS.APS.DUREE'})}
              </Form.Label>
              <Form.Select
                name="duree"
                value={formik.values.duree}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.duree && !!formik.errors.duree}
              >
                <option value="">{intl.formatMessage({id: 'DIVOPS.APS.DUREE.PLACEHOLDER'})}</option>
                {DUREE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              {formik.touched.duree && formik.errors.duree && (
                <Form.Control.Feedback type="invalid">{formik.errors.duree}</Form.Control.Feedback>
              )}
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group>
              <Form.Label className="fw-bold fs-3 required">
                {intl.formatMessage({id: 'DIVOPS.APS.TYPE'})}
              </Form.Label>
              <Form.Select
                name="type"
                value={formik.values.type}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.type && !!formik.errors.type}
              >
                <option value="">{intl.formatMessage({id: 'DIVOPS.APS.TYPE.PLACEHOLDER'})}</option>
                {TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              {formik.touched.type && formik.errors.type && (
                <Form.Control.Feedback type="invalid">{formik.errors.type}</Form.Control.Feedback>
              )}
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <TextAreaInputControlled
              name="informationComplementaire"
              label={intl.formatMessage({id: 'DIVOPS.APS.INFO_COMPLEMENTAIRE'})}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              onReset={formik.handleReset}
              touched={formik.touched.informationComplementaire}
              errors={formik.errors.informationComplementaire}
              value={formik.values.informationComplementaire}
              required={false}
            />
          </Row>

          <Row className="mb-3">
            <FileInputControlled
              name="file"
              value={formik.values.file}
              label={intl.formatMessage({id: 'DIVOPS.APS.ATTACHMENT'})}
              onChange={handleFileChange}
              onBlur={formik.handleBlur}
              onReset={formik.handleReset}
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
              touched={formik.touched.file as boolean | undefined}
              errors={formik.errors.file as string | undefined}
            />
          </Row>

          {errorMessage && (
            <Row className="mb-3">
              <Form.Text className="text-danger">{errorMessage}</Form.Text>
            </Row>
          )}
          <div className="d-flex justify-content-end gap-3">
            <ButtonReset onClick={handleClose} textId="DIVOPS.APS.CANCEL" size="small" />
            <ButtonSubmit
              disabled={formik.isSubmitting}
              isLoading={isLoading}
              textId="DIVOPS.APS.SUBMIT"
              size="small"
              onClick={formik.submitForm}
            />
          </div>
    </>
  );
};

export default ApsRequestForm;
